import { AsyncThunkAction } from '@reduxjs/toolkit'
import { UnknownAction } from 'redux'
import { ThunkDispatch } from 'redux-thunk'

const retryFetchAction = async <Returned, ThunkArg>(
  dispatch: ThunkDispatch<unknown, unknown, UnknownAction>,
  actionCreator: (arg: ThunkArg) => AsyncThunkAction<Returned, ThunkArg, {}>,
  arg: ThunkArg,
  maxRetries: number,
  delay: number,
  abortController?: AbortController,
): Promise<void> => {
  let attempts = 0

  return new Promise<void>((resolve, reject) => {
    const handleAbort = () => {
      reject(new DOMException('Aborted', 'AbortError'))
    }

    if (abortController) {
      abortController.signal.addEventListener('abort', handleAbort, {
        once: true,
      })
    }

    const executeAction = async () => {
      while (attempts < maxRetries) {
        try {
          if (abortController?.signal.aborted) {
            return
          }
          await dispatch(actionCreator(arg)).unwrap()
          resolve()
          return
        } catch (error) {
          if (abortController?.signal.aborted) {
            return
          }
          if (attempts >= maxRetries - 1) {
            console.error(`Max retries reached for ${actionCreator.name}`)
            reject(error)
            return
          }
          attempts++
          await new Promise((resolveDelay) => setTimeout(resolveDelay, delay))
        }
      }
    }

    executeAction()
      .catch(reject)
      .finally(() => {
        if (abortController) {
          abortController.signal.removeEventListener('abort', handleAbort)
        }
        resolve()
        return
      })
  })
}

const splitS3FilePath = (filepath: string) => {
  const splitted = filepath.split('/')
  return {
    s3_folder: splitted[0] ?? '',
    s3_file: splitted[1] ?? '',
  }
}

export { retryFetchAction, splitS3FilePath }
